import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api, fullName } from '../lib/api';
import { telefonoConPrefijo, telefonoParaEnlace } from '../lib/utils';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { Check, ChevronsUpDown, Loader2, ShieldAlert, UserPlus } from 'lucide-react';
import IndicadorProhibido from './IndicadorProhibido';

/**
 * Patient picker with built-in search.
 *
 * Improvements vs a plain <Select>:
 *  - Pre-fetches patients on mount so the list is ready when the user opens it
 *    (no more empty dropdown on first click due to a slow API).
 *  - Clear loading / empty / error states so the user never sees a silent
 *    empty list and can act ("Crea un paciente desde /pacientes").
 *  - Type-to-filter across name, surnames and NIF.
 *  - Optional `patients` prop to inject an already-loaded list (avoids
 *    duplicate fetches when the parent already has the data).
 *
 * The component delegates the loading / error / empty / list states to
 * dedicated subcomponents to keep the main function focused on data-fetching.
 */

// Accent-insensitive lowercase match — used as the Command filter.
const _norm = (s) => (s || '').toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

/**
 * EL AVISO DE LA LOPD SE PINTA CON SU PROPIO FONDO, NO SOLO CON COLOR DE LETRA
 * (2026-09-17). Antes era `text-red-700` a secas, y eso solo se leía encima de
 * blanco (6,47:1). Medido en el navegador con el CSS compilado, en cuanto la
 * fila tenía otro color debajo el aviso desaparecía:
 *   · fila que el desplegable deja ACTIVA —la que el usuario tiene señalada—:
 *     rojo sobre el verde de la casa (--accent, #2f6f5f) = 1,10:1;
 *   · tema oscuro, fila normal: 2,47:1 sobre el popover; fila activa: 1,91:1;
 *     y 2,69:1 en el botón cerrado;
 *   · y el botón cerrado se pone verde también al pasar el ratón (variant
 *     outline → hover:bg-accent), así que allí pasaba lo mismo.
 *
 * Con colores de letra NO se arregla, por mucho `dark:` que se ponga: sobre el
 * verde del tema oscuro (#3b9b83) ni el blanco llega a 4,5:1 (3,39:1), así que
 * el aviso tendría que ir OSCURO en la fila activa... y oscuro desaparece en la
 * fila normal del tema oscuro. La misma clase no puede ser las dos cosas.
 *
 * Por eso el aviso lleva fondo propio: deja de depender de lo que tenga debajo
 * y sale igual en los cuatro sitios — 6,80:1 en claro y 11,16:1 en oscuro. Es
 * el mismo par fondo+letra que ya usan MejorasPage y TareasDireccionPage, con
 * una diferencia a propósito: ellas pueden permitirse `dark:bg-red-950/40`
 * (translúcido) porque van sobre tarjetas quietas; aquí el verde de la fila
 * activa se colaría por ese 40 % y el contraste volvería a depender de la fila
 * (4,77:1 sobre la activa contra 11,16:1). Opaco, y se acabó.
 *
 * Las dos copias —la de la lista y la del botón— comparten esta constante a
 * propósito: si alguien retoca el color, que no quede una legible y otra no.
 */
const AVISO_LOPD =
  'inline-flex shrink-0 items-center rounded-full px-1.5 py-0.5 text-xs font-medium '
  + 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200';

function _LoadingState() {
  return (
    <div className="px-3 py-6 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
      <Loader2 className="w-4 h-4 animate-spin" /> Cargando pacientes…
    </div>
  );
}

/**
 * El aviso de que algo ha fallado, con su Reintentar. Vale para los DOS
 * fallos que puede tener este selector —la carga base y la búsqueda en
 * servidor (2026-09-18)—, y comparten pinta a propósito, igual que las dos
 * copias del aviso de la LOPD: si alguien retoca el bloque, que no quede uno
 * que se ve y otro que no.
 *
 * `testId` es el PREFIJO: la carga base pasa `sel` (→ `sel-error`,
 * `sel-retry`) y la búsqueda pasa `sel-busqueda` (→ `sel-busqueda-error`,
 * `sel-busqueda-retry`), así cada fallo se puede señalar por separado.
 */
function _ErrorState({ title = 'Error cargando pacientes', message, onRetry, testId }) {
  return (
    <div className="px-3 py-4 text-sm" data-testid={`${testId}-error`}>
      <div className="flex items-start gap-2 text-red-700">
        <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
        <div>
          <div className="font-medium">{title}</div>
          <div className="text-xs text-red-600 mb-2">{message}</div>
          <Button size="sm" variant="outline" onClick={onRetry} data-testid={`${testId}-retry`}>
            Reintentar
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * SIN RESPUESTA NO SE AFIRMA QUE ALGO NO EXISTA (18/9/2026).
 *
 * Lo que se lee cuando la búsqueda en SERVIDOR se cae. Antes esa petición
 * llevaba un `.catch(() => {})` «porque la lista local sigue funcionando como
 * fallback», y la lista local son las fichas más recientes por fecha de alta:
 * justo las que NO contienen al paciente antiguo que se está buscando —si
 * estuviera entre ellas no haría falta preguntar al servidor—. Con la
 * petición caída el desplegable se quedaba sin filas que casaran y lo que
 * salía en pantalla era el «Sin coincidencias.» de la lista, que AFIRMA que
 * ese paciente no existe.
 *
 * De ahí se sale por el peor sitio: recepción abre «Generar LOPD» o «Enlace
 * para casa», teclea al paciente antiguo, lee que no coincide nadie y vuelve
 * a crear la ficha que ya estaba, con su LOPD y sus cobros colgando de la
 * nueva. Lo contrario de fusionar duplicados.
 *
 * El título desmiente esa lectura por escrito, no solo dice «ha fallado».
 */
const TITULO_FALLO_BUSQUEDA =
  'No se ha podido buscar en el servidor: esto no quiere decir que el paciente no exista';

function _EmptyState() {
  return (
    <CommandEmpty>
      <div className="px-3 py-4 text-center text-sm">
        <UserPlus className="w-5 h-5 mx-auto mb-1.5 text-muted-foreground" />
        <div className="font-medium text-[#13231E] mb-0.5">Sin pacientes registrados</div>
        <div className="text-xs text-muted-foreground">
          Crea uno primero desde la sección <strong>Pacientes</strong>.
        </div>
      </div>
    </CommandEmpty>
  );
}

function _PatientRow({ patient, selected, onSelect, testId }) {
  const label = fullName(patient) || patient.name || '—';
  const nif = patient.nif || '';
  const phone = patient.phone || '';
  // The Command's filter consumes this string (we composed it with all the
  // searchable fields so typing a NIF or phone also matches).
  // El teléfono entra TRES veces a propósito: como está guardado (para quien
  // teclea las cifras seguidas), como se enseña (para quien lo copia de la
  // ficha y lo pega) y solo cifras (para quien lo pega desde fuera con otros
  // huecos). Buscar es lo único que no puede volverse más estricto.
  const searchString = `${label} ${nif} ${phone} ${telefonoConPrefijo(phone)} ${telefonoParaEnlace(phone)}`;
  return (
    <CommandItem value={searchString} onSelect={onSelect} data-testid={testId}>
      <Check className={`mr-2 h-4 w-4 ${selected ? 'opacity-100' : 'opacity-0'}`} />
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">{label}</div>
        {/*
          EL AVISO DE LA LOPD NO SE TRUNCA Y VA DELANTE (2026-09-17).
          Antes esta línea era un solo bloque con `truncate` —NIF · teléfono ·
          Pendiente LOPD— y lo que se cortaba por la derecha era justo el
          aviso rojo: desde que el teléfono se pinta con sus huecos
          («+34 600 12 34 56», 16 caracteres en vez de 9) en el diálogo de
          cobro rápido —dos columnas dentro de un max-w-2xl, así que el
          desplegable mide media pantalla— y en móvil ya no cabía. Un
          paciente sin LOPD firmada se veía exactamente igual que uno con
          ella, y el aviso es lo ÚNICO que lo dice.

          Ahora son dos trozos en un flex: el aviso con `shrink-0` (nunca
          cede ancho) y los datos con `min-w-0 truncate` (ceden ellos). Va
          DELANTE por dos motivos: el recorte siempre come por la derecha,
          así que el primer hueco es el más seguro que hay; y al quedar
          pegado al mismo borde en todas las filas se lee la lista de un
          vistazo, cosa que no pasa si flota detrás de un NIF de largo
          variable. Lo prescindible es el NIF o el teléfono —que están en
          la ficha—; el aviso de que no ha firmado, no.
        */}
        <div className="flex items-baseline gap-1.5 text-xs text-muted-foreground">
          {/* Paciente PROHIBIDO (14/10/2026): delante de todo y sin ceder
              ancho, como el aviso de la LOPD. Sin botón dentro de la fila: el
              motivo, en el título. */}
          <IndicadorProhibido paciente={patient} tamano="pequeno" interactivo={false}
            testId={`${testId}-prohibido`} />
          {!patient.has_lopd && (
            <span className={AVISO_LOPD} data-testid={`${testId}-lopd`}>
              Pendiente LOPD
            </span>
          )}
          <span className="min-w-0 truncate">
            {nif || '—'}{phone ? ` · ${telefonoConPrefijo(phone)}` : ''}
          </span>
        </div>
      </div>
    </CommandItem>
  );
}

function _PatientList({ patients, value, onSelectId, testId, sinRespuesta = false }) {
  return (
    <>
      {/* «Sin coincidencias.» solo cuando el servidor ha contestado. Si la
          búsqueda se cayó (`sinRespuesta`), lo que se pinta arriba es el aviso
          del fallo: esta frase afirmaría que el paciente no existe sin haber
          podido mirar (18/9/2026). */}
      {!sinRespuesta && <CommandEmpty>Sin coincidencias.</CommandEmpty>}
      <CommandGroup>
        {patients.map((p) => (
          <_PatientRow
            key={p.id}
            patient={p}
            selected={value === p.id}
            onSelect={() => onSelectId(p.id)}
            testId={`${testId}-item`}
          />
        ))}
      </CommandGroup>
    </>
  );
}

export function PatientCombobox({
  value,
  onChange,
  patients: externalPatients,
  placeholder = 'Selecciona paciente…',
  dataTestid = 'patient-combobox',
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [internalPatients, setInternalPatients] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  // Búsqueda en SERVIDOR mientras se teclea: la lista base solo trae los
  // pacientes más recientes (límite del backend), así que sin esto los
  // pacientes antiguos eran imposibles de encontrar ("no aparece el
  // paciente" al hacer un presupuesto, aunque exista y tenga LOPD).
  const [search, setSearch] = useState('');
  const [serverResults, setServerResults] = useState([]);
  // Y si esa búsqueda se cae, se dice (18/9/2026): antes era un catch vacío y
  // el silencio se leía como «ese paciente no existe». Ver TITULO_FALLO_BUSQUEDA.
  const [errorBusqueda, setErrorBusqueda] = useState(null);
  // Cambiarlo es lo que vuelve a lanzar la búsqueda al pulsar «Reintentar»
  // (el texto tecleado no ha cambiado, así que el efecto no se repetiría solo).
  const [reintentoBusqueda, setReintentoBusqueda] = useState(0);

  const fetchPatients = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/patients');
      setInternalPatients(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'No se pudieron cargar los pacientes');
      setInternalPatients([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Only fetch internally when the parent didn't provide a list.
  useEffect(() => {
    if (externalPatients) return;
    fetchPatients();
  }, [externalPatients, fetchPatients]);

  // Debounce de la búsqueda en servidor (300 ms, mínimo 2 caracteres).
  useEffect(() => {
    const q = search.trim();
    if (q.length < 2) {
      setServerResults([]);
      setErrorBusqueda(null);
      return undefined;
    }
    // `vigente` descarta la respuesta de una búsqueda que ya no es la que hay
    // escrita: sin esto, un fallo tardío de «Dol» pintaría el aviso encima de
    // los resultados buenos de «Dolores».
    let vigente = true;
    const t = setTimeout(() => {
      api.get('/api/patients', { params: { q, limit: 100 } })
        .then((res) => {
          if (!vigente) return;
          setServerResults(Array.isArray(res.data) ? res.data : []);
          setErrorBusqueda(null);
        })
        .catch((e) => {
          if (!vigente) return;
          // NO se traga el fallo: la lista local no es un fallback para esto
          // —son las fichas más recientes, justo donde no está el paciente
          // antiguo que se busca—, así que se avisa y se ofrece reintentar.
          // Los resultados que hubiera no se borran: lo que ya se encontró
          // sigue siendo válido y quitarlo solo escondería fichas de verdad.
          setErrorBusqueda(e?.response?.data?.detail || e.message || 'No se pudo consultar el servidor');
        });
    }, 300);
    return () => { vigente = false; clearTimeout(t); };
  }, [search, reintentoBusqueda]);

  const basePatients = externalPatients || internalPatients || [];
  // Mezcla lista base + resultados del servidor (sin duplicados por id).
  const patients = useMemo(() => {
    if (serverResults.length === 0) return basePatients;
    const seen = new Set(basePatients.map((p) => p.id));
    return [...basePatients, ...serverResults.filter((p) => !seen.has(p.id))];
  }, [basePatients, serverResults]);
  const selected = useMemo(() => patients.find((p) => p.id === value), [patients, value]);

  const triggerLabel = selected
    ? `${fullName(selected) || selected.name || '—'}${selected.nif ? ` · ${selected.nif}` : ''}`
    : placeholder;

  // Devolvemos también la ficha elegida, no solo el id: el paciente puede venir
  // de la búsqueda en servidor y NO estar en la lista que el padre precargó
  // (que son solo los 200 más recientes de varios miles). Quien resolvía el id
  // contra su lista local se quedaba sin nombre ni NIF —o directamente perdía
  // la selección— al elegir un paciente antiguo.
  const onSelectId = useCallback((id) => {
    onChange?.(id, patients.find((p) => p.id === id) || null);
    setOpen(false);
  }, [onChange, patients]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={dataTestid}
          className="w-full justify-between font-normal"
        >
          {/*
            Y con el desplegable CERRADO también. Enseñar el aviso solo
            dentro de la lista lo pierde en el peor momento: se elige al
            paciente, la lista se cierra y lo único que queda en pantalla
            mientras se cobra o se firma es «nombre · NIF». Aquí el nombre
            sí manda —hay que poder comprobar a quién se ha elegido—, así
            que el aviso va detrás, pero con `shrink-0`: quien cede ancho al
            estrecharse es el nombre, nunca el aviso.
          */}
          <span className="flex min-w-0 items-center gap-2">
            <span className={`truncate ${!selected ? 'text-muted-foreground' : ''}`}>{triggerLabel}</span>
            {selected && (
              <IndicadorProhibido paciente={selected} tamano="pequeno" interactivo={false}
                testId={`${dataTestid}-prohibido`} />
            )}
            {selected && !selected.has_lopd && (
              <span className={AVISO_LOPD} data-testid={`${dataTestid}-lopd`}>
                Pendiente LOPD
              </span>
            )}
          </span>
          {loading
            ? <Loader2 className="w-4 h-4 ml-2 opacity-60 animate-spin shrink-0" />
            : <ChevronsUpDown className="w-4 h-4 ml-2 opacity-50 shrink-0" />}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command filter={(itemValue, search) => (_norm(itemValue).includes(_norm(search)) ? 1 : 0)}>
          <CommandInput
            placeholder="Buscar por nombre o NIF…"
            value={search}
            onValueChange={setSearch}
            data-testid={`${dataTestid}-input`}
          />
          <CommandList>
            {loading && <_LoadingState />}
            {!loading && error && (
              <_ErrorState message={error} onRetry={fetchPatients} testId={dataTestid} />
            )}
            {/* El fallo de la BÚSQUEDA va arriba y aparte del de la carga base:
                la lista base puede estar perfectamente cargada y ser el
                servidor el que no ha contestado a lo que se está tecleando
                (18/9/2026). */}
            {!loading && !error && errorBusqueda && (
              <_ErrorState
                title={TITULO_FALLO_BUSQUEDA}
                message={errorBusqueda}
                onRetry={() => setReintentoBusqueda((n) => n + 1)}
                testId={`${dataTestid}-busqueda`}
              />
            )}
            {/* «Sin pacientes registrados» también es una afirmación: no se
                pinta mientras haya una búsqueda sin respuesta. */}
            {!loading && !error && !errorBusqueda && patients.length === 0 && <_EmptyState />}
            {!loading && !error && patients.length > 0 && (
              <_PatientList
                patients={patients}
                value={value}
                onSelectId={onSelectId}
                testId={dataTestid}
                sinRespuesta={!!errorBusqueda}
              />
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
